<script lang="ts" setup>
import { graphql } from '@/gql';
import AppMarkdown from 'theme/components/atoms/AppMarkdown.vue';
import AccordionContainer from 'theme/components/molecules/AccordionContainer.vue';
import SimplePageContainer from 'theme/components/molecules/SimplePageContainer.vue';

const { data } = useAsyncQuery(
  graphql(`
    query FaqPageTerm {
      term(type: SECRECY_OF_COMMUNICATIONS) {
        title
        body
      }
    }
  `)
);

const term = computed(() => {
  return data.value?.term;
});

useHeadSafe({
  title: 'よくあるご質問',
});
</script>

<template>
  <SimplePageContainer>
    <main class="page">
      <header>
        <h2 class="page__heading">よくあるご質問</h2>
      </header>

      <section>
        <AccordionContainer class="faq-item">
          Q. オカネコの利用方法を教えてください。
          <template #content>
            <p>
              A.
              オカネコはいくつかの質問に答えることで、あなたのお金の健康状態を判定し、診断結果をもとにお金のプロであるアドバイザーと、お金の不安や疑問についてチャットで相談することができるサービスです。
              スマホやPCから診断を受けたら、会員登録をして診断結果をご確認ください。
            </p>
            <p>
              登録後、あなたの診断結果と記入した相談内容に対してアドバイザーからコメントが届きます。
              「話を聞いてみたい」と思うアドバイザーがいれば、チャット相談を始めてみてください。
            </p>
            <p>
              どのように相談すればいいかわからないという方は、以下の記事を参考にしてください。<br /><a
                href="https://mag.okane-kenko.jp/entry/2020/12/11/155102"
                target="_blank"
                >「オカネコ」使い方を簡単解説！FPに聞くべき6つの質問</a
              >
            </p>
          </template>
        </AccordionContainer>

        <AccordionContainer class="faq-item">
          Q. オカネコは他のFP相談とどう違うのですか？
          <template #content>
            <p>
              A.
              オカネコは100社500名以上のFPやIFAが登録しており、証券・保険・銀行などさまざまなアドバイスをワンストップで相談できる日本最大級のプラットフォームです。<br />そのため資産運用、保険、住宅ローン、相続など幅広い相談ができ、複数のアドバイザーのコメントを比較できる点が大きな特徴です。
            </p>
            <p>
              チャット相談なので、自宅や移動時間などスキマ時間に利用できたり効率よく利用できます。
            </p>
          </template>
        </AccordionContainer>

        <AccordionContainer class="faq-item">
          Q. オカネコはなぜ無料で利用できるのですか？どこから有料になりますか？
          <template #content>
            <p>
              A.
              オカネコはご登録いただいているアドバイザーが月額で課金するビジネスモデルのため、ユーザーの皆様は無料でチャットをご利用いただけます。回数や人数の制限はなく、チャット相談はずっと無料です。
            </p>
            <p>
              ただし、チャット以外の料金についてはアドバイザーによって異なります。
              基本的には金融商品や保険を購入した場合にかかる手数料がアドバイザー側の報酬となるため、面談は無料なことが多いですが、有料相談を行っているアドバイザーもおりますので、気になる方はチャットでどこから有料になるか直接アドバイザーにお聞きください。
            </p>
          </template>
        </AccordionContainer>

        <AccordionContainer class="faq-item">
          Q.チャット相談中にアドバイザーから金融商品や保険の営業をかけられないか心配です。
          <template #content>
            <p>
              A.
              お客様から具体的なご依頼がない限りは、アドバイザーからお客様へ商品を提案することはございませんのでご安心ください。
            </p>
          </template>
        </AccordionContainer>

        <AccordionContainer class="faq-item">
          Q. LINE・メール配信を止めるにはどうすればいいですか？
          <template #content>
            <p>
              A. 診断結果に対するアドバイスのコメントの
              LINE・メール通知はこちらの<NuxtLink
                :to="{ path: '/settings/notification' }"
                >設定ページ</NuxtLink
              >
              （ログインした状態でアクセスしてください。）から止めることができます。<br />
              弊社からの配信を全て停止する場合、カスタマーサポートまでお問い合わせください。<br /><br />※LINEでご登録いただいている場合はLINEのフォロー解除で停止いただくことができます。
            </p>
          </template>
        </AccordionContainer>

        <AccordionContainer class="faq-item">
          Q. 退会するにはどうすればいいですか？
          <template #content>
            <p>
              A. 退会をご希望の場合は、<a href="mailto:support@okane-kenko.jp"
                >support@okane-kenko.jp</a
              >宛にメールをお送りください。メールを確認後、3営業日以内で退会の手続きをいたします。
            </p>
          </template>
        </AccordionContainer>

        <AccordionContainer class="faq-item">
          Q. 個人情報の取扱いについて教えてください。
          <template #content>
            <p>
              A.
              オカネコでは、ご本人様の同意なく、個人情報を外部企業に開示することはございません。また、個人情報の取り扱いに関しても、株式会社400Fのプライバシーポリシーに則って適切な安全対策を講じます。詳しくは、株式会社400Fの
              <NuxtLink :to="{ path: '/policies/privacy' }"
                >株式会社400Fのプライバシーポリシー</NuxtLink
              >をご参照ください。
            </p>
          </template>
        </AccordionContainer>

        <AccordionContainer v-if="term" class="faq-item">
          Q.
          通信の秘密の内容と、通信の秘密への同意を撤回する方法を教えて下さい。
          <template #content>
            <AppMarkdown :body="term.body" />
          </template>
        </AccordionContainer>
      </section>
    </main>
  </SimplePageContainer>
</template>

<style lang="scss" scoped>
.page {
  max-width: 480px;
  margin: 0 auto;
  font-size: 15px;

  &__heading {
    margin-bottom: 24px;
    font-size: 24px;
    text-align: center;
  }
}

.faq-item {
  padding: 15px;
  border: 1px solid $color-border-low-emphasis;
  margin-top: 20px;
  border-radius: 8px;
  box-shadow: $shadow-4;
}
</style>
